// src/api/auth.ts
import api from "./client";
import {
  sendSignInLinkToEmail,
  signInWithEmailLink,
  isSignInWithEmailLink,
} from "firebase/auth";
import { auth } from "../firebase";
import { config } from "../config";

const REQUIRE_REGISTRATION_TOKEN = config.featureFlags.requireRegistrationToken;

const EMAIL_FOR_SIGN_IN_KEY = "emailForSignIn";

export const sendMagicLink = async (email: string): Promise<void> => {
  const actionCodeSettings = {
    url: `${window.location.origin}/auth/email/callback`,
    handleCodeInApp: true,
  };
  await sendSignInLinkToEmail(auth, email, actionCodeSettings);
  localStorage.setItem(EMAIL_FOR_SIGN_IN_KEY, email);
};

export const completeMagicLinkSignIn = async (
  email: string,
  link: string
): Promise<void> => {
  if (!isSignInWithEmailLink(auth, link)) {
    throw new Error("Invalid or expired magic link");
  }
  await signInWithEmailLink(auth, email, link);
  localStorage.removeItem(EMAIL_FOR_SIGN_IN_KEY);
};

export const getStoredEmailForSignIn = (): string | null =>
  localStorage.getItem(EMAIL_FOR_SIGN_IN_KEY);

export const clearStoredEmailForSignIn = (): void => {
  localStorage.removeItem(EMAIL_FOR_SIGN_IN_KEY);
};

export const saveOnboardingStep = async (payload: {
  step: string;
  step_status: "complete" | "skipped" | "incomplete";
  complete_wizard?: boolean;
}) => {
  const res = await api.post("/saveOnboardingStep", payload);
  return res.data;
};

export const getOnboardingStatus = async () => {
  const res = await api.get("/getOnboardingStatus");
  return res.data;
};

export const checkSlugAvailability = async (slug: string) => {
  const res = await api.get("/checkSlugAvailability", { params: { slug } });
  return res.data as { available: boolean; slug: string; reason?: string };
};

export const cancelOnboarding = async (): Promise<void> => {
  await api.delete("/cancelOnboarding");
};

export const isUserRegistered = async () => {
  try {
    const res = await api.get("/isRegistered");
    return res.data;
  } catch (error) {
    console.error("❌ isUserRegistered error:", error);
    return { registered: false };
  }
};

export const getBusinessId = async () => {
  try {
    const res = await api.get("/getBusinessId");
    return res.data;
  } catch (error) {
    console.error("❌ getBusinessId error:", error);
    return null;
  }
};

export async function registerNewBusiness(payload: {
  business_name: string;
  business_mode: string;
  address: string;
  phone: string;
  description: string;
  slug: string;
  timezone?: string;
  country_code?: string;
  currency?: string;
  registration_token?: string;
  email?: string;
}) {
  try {
    const registration_token = (payload.registration_token ?? "").trim();

    if (REQUIRE_REGISTRATION_TOKEN && !registration_token) {
      throw { status: 400, data: { error: "Missing registration code" }, message: "Missing registration code" };
    }

    const trimmed = {
      business_name: payload.business_name.trim(),
      business_mode: payload.business_mode.trim(),
      address: payload.address.trim(),
      phone: payload.phone.trim(),
      description: payload.description.trim(),
      slug: payload.slug.trim(),
      timezone: payload.timezone?.trim(),
      country_code: payload.country_code?.trim(),
      currency: payload.currency?.trim(),
      email: payload.email?.trim(),
      ...(registration_token ? { registration_token } : {}),
    };

    const res = await api.post("/signOn", trimmed);
    return res.data;
  } catch (error: unknown) {
    // ✅ Normalize Axios errors so the UI can reliably read status + data.error
    const err = error as {
      response?: { status?: number; data?: { error?: string } };
      status?: number;
      data?: { error?: string } | null;
      message?: string;
    };
    const status = err?.response?.status ?? err?.status ?? 0;
    const data = err?.response?.data ?? err?.data ?? null;

    const normalized = {
      status,
      data,
      message:
        data?.error ||
        err?.message ||
        "Request failed",
      raw: error,
    };

    console.error("❌ registerNewBusiness error:", normalized);
    throw normalized;
  }
}

export const createCategory = async (payload: { name: string; sort_order?: number }) => {
  const res = await api.post("/manageServiceCategories", payload);
  return res.data as { service_category_id: string; name: string; sort_order: number };
};

export const getCategories = async () => {
  const res = await api.get("/manageServiceCategories");
  return res.data as { service_category_id: string; name: string; sort_order: number }[];
};

export const createService = async (payload: {
  service_name: string;
  duration_minutes: number;
  price: number;
  description?: string;
  category_id?: string;
}) => {
  const res = await api.post("/manageBusinessServices", payload);
  return res.data as { service_id: string; service_name: string };
};

export const createStaffMember = async (payload: {
  full_name: string;
  bio?: string;
  is_active?: boolean;
}) => {
  const res = await api.post("/manageBusinessStaff", payload);
  return res.data as { staff_id: string; full_name: string };
};

export type DeleteAccountRequest = {
  confirmation: "delete";
};

export const deleteAccount = async (payload: DeleteAccountRequest) => {
  try {
    const res = await api.post("/deleteAccount", payload);
    return res.data;
  } catch (error) {
    console.error("❌ deleteAccount error:", error);
    throw error;
  }
};
